<template>
  <SrpModal :isVisible="props.isVisible" size="medium" className="nearby-suggestion-edit-modal" :isClosable="!busy" :isCloseButtonVisible="!busy" @update:isVisible="emit('update:isVisible', $event)">
    <template #header><h2 class="global-h2">Edit suggestion</h2></template>
    <template #content>
      <div v-if="editing" class="nearby-suggestion-edit ui form">
        <label>Name <input v-model.trim="editing.displayName" /></label>
        <label>Website <input v-model.trim="editing.websiteUri" type="url" /></label>
        <label v-if="editing.tagKind === 'Location'">Address <input v-model.trim="editing.address" /></label>
        <div v-if="editing.tagKind === 'Location'" class="nearby-suggestion-edit__category">
          <span class="nearby-suggestion-edit__category-label">Category</span>
          <LocationCategoryPicker v-model="category" :showLabel="true" :allowClear="false" />
        </div>
        <template v-if="editing.excludeFromIndex.event">
          <label>Dates or typical timing <input v-model="editing.excludeFromIndex.event.date" /></label>
          <label>Event source <input v-model.trim="editing.excludeFromIndex.event.url" type="url" /></label>
        </template>
        <p v-if="error" class="global-error-string">{{ error }}</p>
      </div>
    </template>
    <template #footer>
      <SrpButton color="gray" fill="outlined" :isDisabled="busy" @click="emit('update:isVisible', false)">Cancel</SrpButton>
      <SrpButton :isDisabled="busy || !editing?.displayName?.trim()" @click="save">Save changes</SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import LocationCategoryPicker from "@components/SocialLocations/LocationCategoryPicker.vue";
import NearbyLocationsRepo from "@repos/NearbyLocationsRepo";
import { nearbyErrorMessage } from "@logic/NearbySuggestions";
import { categoryOptionFor } from "@logic/LocationCategories";
import type { NearbySuggestion } from "@contracts/nearbyLocations";
import type { LocationCategory } from "@contracts/socialLocations";

const props = withDefaults(defineProps<{ isVisible: boolean; customerId: string; suggestion?: NearbySuggestion | null }>(), { suggestion: null });
const emit = defineEmits<{
  (e: "update:isVisible", value: boolean): void;
  (e: "saved", suggestion: NearbySuggestion): void;
}>();

/** Working copy, so cancelling leaves the list untouched. */
const editing = ref<NearbySuggestion | null>(null);
const busy = ref(false);
const error = ref("");
const category = computed<LocationCategory | null>({
  get: () => (categoryOptionFor("Location", editing.value?.aiCategory)?.value as LocationCategory) ?? "Other",
  set: value => { if (editing.value) editing.value.aiCategory = value ?? "Other"; },
});

watch(
  () => [props.isVisible, props.suggestion?.id] as const,
  ([visible]) => {
    if (!visible || !props.suggestion) return;
    error.value = "";
    editing.value = JSON.parse(JSON.stringify(props.suggestion));
    if (editing.value?.tagKind === "Location") editing.value.aiCategory = categoryOptionFor("Location", editing.value.aiCategory)?.value ?? "Other";
  },
  { immediate: true }
);

async function save() {
  const row = editing.value;
  if (!row || busy.value) return;
  busy.value = true;
  error.value = "";
  try {
    const saved = await NearbyLocationsRepo.update(props.customerId, row.id, {
      eTag: row._etag,
      displayName: row.displayName,
      websiteUri: row.websiteUri,
      formattedAddress: row.address,
      category: row.tagKind === "Location" ? row.aiCategory : undefined,
      eventDate: row.excludeFromIndex.event?.date,
      eventUrl: row.excludeFromIndex.event?.url,
    });
    emit("update:isVisible", false);
    emit("saved", saved);
  } catch (caught) {
    error.value = nearbyErrorMessage(caught);
  } finally {
    busy.value = false;
  }
}
</script>

<style scoped>
.nearby-suggestion-edit { display: grid; gap: 15px; }
.nearby-suggestion-edit label { display: grid; gap: 6px; font-weight: 600; }
.nearby-suggestion-edit input { box-sizing: border-box; width: 100%; max-width: 100%; }
.nearby-suggestion-edit__category { display: grid; gap: 6px; justify-items: start; }
.nearby-suggestion-edit__category-label { font-weight: 600; }
.nearby-suggestion-edit p { margin: 0; }
</style>

<style>
.nearby-suggestion-edit-modal .srp-modal__content { box-sizing: border-box; width: 100%; }
</style>
